import React, { useEffect, useState, useMemo } from 'react';
import { HiChevronDoubleRight } from 'react-icons/hi';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getUserPersonalDetails, DeleteMyProfile } from '../../Redux/Actions/UsersProfileAction';
import { Skeleton } from '@mui/material';
import { toast } from 'react-toastify';
import useTitle from '../../Hooks/useTitle';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { FaArrowLeft, FaFacebook, FaTrash } from 'react-icons/fa';
import { FaInstagram, FaLinkedin, FaSnapchatGhost, FaTwitter } from "react-icons/fa";
import { MdVerified } from "react-icons/md";

const MyProfile = () => {
  useTitle('My Profile')
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const UsersProfile = useSelector((state) => state.UsersProfile || {});
  const [profileData, setProfileData] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Delete reasons
  const deleteReasons = [
    "Found my partner elsewhere",
    "Privacy concerns",
    "Not satisfied with the service",
    "Taking a break from matrimony search",
    "Other reason"
  ];

  useEffect(() => {
    const fetchUserPersonalDetails = async () => {
      try {
        await dispatch(getUserPersonalDetails());
      } catch (error) {
        toast.error(error?.response?.data?.message);
      }
    }

    fetchUserPersonalDetails();
  }, [dispatch]);

  useEffect(() => {
    if (UsersProfile?.personalDetails) {
      console.log("API Response:", UsersProfile.personalDetails);
      setProfileData(UsersProfile.personalDetails.data);
    }
  }, [UsersProfile]);

  const handleDeleteProfile = async () => {
    if (!selectedReason) {
      toast.error('Please select a reason for deletion');
      return;
    }

    if (selectedReason === 'Other reason' && !otherReason.trim()) {
      toast.error('Please specify your reason');
      return;
    }

    setIsDeleting(true);
    try {
      const finalReason = selectedReason === 'Other reason' ? otherReason : selectedReason;
      
      const result = await dispatch(DeleteMyProfile(profileData?.id, finalReason));
      
      if (result?.data?.success) {
        toast.success('Profile deleted successfully');
        setDeleteModalOpen(false);
        
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        
        navigate('/');
        window.location.reload();
      } else {
        throw new Error(result?.data?.message || 'Failed to delete profile');
      }
      
    } catch (error) {
      console.error('Delete profile error:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to delete profile');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenDeleteModal = () => {
    setSelectedReason('');
    setOtherReason('');
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedReason('');
    setOtherReason('');
  };

  const galleryImages = useMemo(() => {
    return profileData?.user_images?.map((img) =>
      `https://matrimony.remotedev.pro/${img.image_path}`
    ) || [];
  }, [profileData]);

  const fullName = useMemo(() => {
    return profileData
      ? `${profileData.firstname || "N/A"} ${profileData.lastname || "N/A"}`
      : "N/A";
  }, [profileData]);

  // Function to get sender image URL
  const getSenderImageUrl = () => {
    if (profileData?.other_details?.sender_image) {
      return `${profileData.other_details.sender_image}`;
    }
    return null;
  };

  const senderImageUrl = getSenderImageUrl();

  const DetailItem = ({ label, value, isRemoveDots = false }) => (
    <div className="flex gap-3 items-center sm:text-[17px] leading-6 py-1">
      <div className="w-[22px]">
        <HiChevronDoubleRight size={22} style={{ color: '#E72848' }} />
      </div>
      <div>
        <span className="text-[#777777] font-[500]">
          {label}{label && !isRemoveDots ? ":" : ""}
        </span> <span className='font-[500] text-[#151515]'>{value || "N/A"}</span>
      </div>
    </div>
  );

  // Reusable component for sender image with photo view
  const SenderImageItem = () => {
    if (!senderImageUrl) return null;

    return (
      <div className="flex gap-3 items-center sm:text-[17px] leading-6 py-1">
        <div className="w-[22px]">
          <HiChevronDoubleRight size={22} style={{ color: '#E72848' }} />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[#777777] font-[500]">Sender Image:</span>
          <PhotoProvider>
            <PhotoView src={senderImageUrl}>
              <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-gray-200 cursor-pointer hover:border-[#E72848] transition-all duration-200">
                <img
                  src={senderImageUrl}
                  alt="Sender"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                />
              </div>
            </PhotoView>
          </PhotoProvider>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 animate-scale-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Delete Profile</h3>
              <button
                onClick={handleCloseDeleteModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isDeleting}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-gray-600 mb-4">
              Are you sure you want to delete your profile? This action cannot be undone.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Please select a reason *
              </label>
              <div className="space-y-2">
                {deleteReasons.map((reason) => (
                  <label key={reason} className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="deleteReason"
                      value={reason}
                      checked={selectedReason === reason}
                      onChange={(e) => setSelectedReason(e.target.value)}
                      className="w-4 h-4 text-red-600 focus:ring-red-500"
                      disabled={isDeleting}
                    />
                    <span className="text-sm text-gray-700">{reason}</span>
                  </label>
                ))}
              </div>
            </div>

            {selectedReason === 'Other reason' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Please specify your reason *
                </label>
                <textarea
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  placeholder="Enter your reason here..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  disabled={isDeleting}
                />
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCloseDeleteModal}
                disabled={isDeleting}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProfile}
                disabled={isDeleting || !selectedReason || (selectedReason === 'Other reason' && !otherReason.trim())}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <FaTrash size={14} />
                    Delete Profile
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {UsersProfile.loading ? (
        <>
          {/* Breadcrumb Skeleton */}
          <div className="max-w-screen-xl mx-auto px-4 py-5">
            <Skeleton variant="text" width="33%" height={30} />
          </div>

          <div className="max-w-screen-xl mx-auto px-4 py-5">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Gallery Section Skeleton */}
              <div className="w-full md:w-1/2 grid grid-cols-2 gap-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="rectangular"
                    width="100%"
                    height={200}
                    className="rounded-xl"
                  />
                ))}
              </div>

              {/* Detail Section Skeleton */}
              <div className="w-full md:w-1/2">
                {/* Header: Profile Name & Button */}
                <div className="flex justify-between items-center mb-6">
                  <Skeleton variant="text" width="50%" height={40} />
                  <Skeleton variant="rectangular" width={100} height={40} className="rounded" />
                </div>

                {/* Detail Items */}
                <div className="mb-6">
                  {/* Section Title Skeleton */}
                  <Skeleton variant="text" width="30%" height={30} className="mb-4" />

                  {/* Repeated Detail Items Skeleton (using text variant only) */}
                  {Array.from({ length: 10 }).map((_, index) => (
                    <div key={index} className="mb-3">
                      <Skeleton variant="text" width="100%" height={24} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Breadcrumb */}
          <div className="max-w-screen-xl mx-auto px-2 sm:px-4 py-4">
            <div className="flex justify-between flex-wrap gap-2">
              <div className='flex gap-2 items-center'>
                <div className='flex'>
                  <button
                    onClick={() => window.history.back()}
                    className="hover:text-[#E72848] transition-colors flex items-center group"
                  >
                    <FaArrowLeft
                      size={18}
                      className="group-hover:-translate-x-0.5 transition-transform"
                    />
                  </button>
                </div>
                <div className="text-[18px] sm:text-[22px] font-[600] flex items-center gap-2">
                  {fullName}
                  {profileData?.status === 'approved' && (
                    <MdVerified size={24} className="text-[#e72848]" title="Verified" />
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <NavLink
                  to="/profile/Edit Profile"
                  className="bg-[#E72848] text-white py-1 text-[14px] flex justify-center items-center sm:text-[15px] font-[500] px-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02] shadow-md hover:shadow-lg"
                >
                  Edit Now
                </NavLink>
                <button
                  onClick={handleOpenDeleteModal}
                  className="bg-gray-600 text-white py-1 text-[14px] flex justify-center items-center gap-2 sm:text-[15px] font-[500] px-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02] shadow-md hover:shadow-lg hover:bg-gray-700"
                >
                  <FaTrash size={14} />
                  Delete
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-screen-xl mx-auto px-2 sm:px-4 sm:py-2">
            <div className="grid md:grid-cols-2 gap-5 items-start">
              {/* Gallery Section */}
              <div className="grid grid-cols-2 w-full gap-2 sm:gap-6">
                <PhotoProvider>
                  {galleryImages.map((src, index) => (
                    <div
                      key={index}
                      className="relative overflow-hidden rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      <PhotoView key={index} src={src}>
                        <img
                          src={src}
                          alt={`User ${index + 1}`}
                          className='w-full object-cover aspect-square transform custom-img-h transition-all duration-500 ease-in-out hover:scale-105'
                          style={{
                            animationDelay: `${index * 100}ms`
                          }}
                        />
                      </PhotoView>
                    </div>
                  ))}
                </PhotoProvider>
              </div>

              {/* Detail Section */}
              <div className='flex flex-col custom-short'>
                <div className="overflow-auto animate-fade-in-side">
                  {/* Personal Details */}
                  <div className="text-xl font-semibold pb-2">Personal Details</div>
                  <div className="lg:flex">
                    <div className="w-[300px] flex flex-col gap-2">
                      <DetailItem label="Gender" value={profileData?.profile?.gender} />
                      <DetailItem label="City" value={profileData?.profile?.city} />
                      <DetailItem label="Country" value={profileData?.profile?.country} />
                      <DetailItem label="Religion" value={profileData?.profile?.religion} />
                      <DetailItem label="Cast" value={profileData?.profile?.cast} />
                      <DetailItem label="Diet" value={profileData?.profile?.diet} />
                      <DetailItem label="Weight" value={profileData?.profile?.weight} />
                      <DetailItem label="Nationality" value={profileData?.profile?.nationality} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <DetailItem label="Marital Status" value={profileData?.profile?.marital_status} />
                      {
                        profileData?.profile?.visa_status && (
                          <DetailItem label="Visa Status" value={profileData?.profile?.visa_status} />
                        )
                      }
                      {
                        profileData?.profile?.years_in_country && (
                          <DetailItem label="#Of Years In Country" value={profileData?.profile?.years_in_country} />
                        )
                      }
                      <DetailItem label="Date of Birth" value={
                        `${new Date(profileData?.profile?.date_of_birth).getDate()} ` +
                        `${new Date(profileData?.profile?.date_of_birth).toLocaleString('default', { month: 'short' })}, ` +
                        `${new Date(profileData?.profile?.date_of_birth).getFullYear()}`
                      }
                      />
                      <DetailItem label="Height" value={profileData?.profile?.height} />
                      {/* <DetailItem label="Native Place" value={profileData?.profile?.native_place} /> */}
                    </div>
                  </div>
                  {/* Other Details */}
                  <div className="flex flex-col gap-2 pt-1">
                    <DetailItem label="Hobbies" value={profileData?.profile?.hobbies} />
                    <DetailItem label="Extra Skills" value={profileData?.profile?.extra_skills} />
                    <DetailItem label="I live with my family" value={(profileData?.profile?.live_with_family === 1 || profileData?.profile?.live_with_family === "1") ? 'Yes' : 'No'} />
                    <DetailItem label="Phone Number" value={profileData?.profile?.phone_number ? `${profileData.profile?.country_code || ""} ${profileData?.profile?.phone_number}` : null} />

                    {/* Sender Image */}
                    <SenderImageItem />

                    {profileData?.other_details?.instagram && (
                      <DetailItem
                        isRemoveDots={true}
                        label={<FaInstagram className="inline text-pink-500" />}
                        value={
                          <a
                            href={`https://instagram.com/${profileData.other_details.instagram}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline "
                          >
                            @{profileData.other_details.instagram}
                          </a>
                        }
                      />
                    )}

                    {profileData?.other_details?.linkedin && (
                      <DetailItem
                        isRemoveDots={true}
                        label={<FaLinkedin className="inline text-blue-700" />}
                        value={
                          <a
                            href={`https://linkedin.com/in/${profileData.other_details.linkedin}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            @{profileData.other_details.linkedin}
                          </a>
                        }
                      />
                    )}

                    {profileData?.other_details?.snapchat && (
                      <DetailItem
                        isRemoveDots={true}
                        label={<FaSnapchatGhost className="inline text-yellow-500" />}
                        value={
                          <a
                            href={`https://www.snapchat.com/add/${profileData.other_details.snapchat}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            @{profileData.other_details.snapchat}
                          </a>
                        }
                      />
                    )}

                    {profileData?.other_details?.twitter && (
                      <DetailItem
                        isRemoveDots={true}
                        label={<FaTwitter className="inline text-sky-500" />}
                        value={
                          <a
                            href={`https://twitter.com/${profileData.other_details.twitter}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            @{profileData.other_details.twitter}
                          </a>
                        }
                      />
                    )}
                    {profileData?.other_details?.facebook && (
                      <DetailItem
                        isRemoveDots={true}
                        label={<FaFacebook className="inline text-sky-500" />}
                        value={
                          <a
                            href={`https://facebook.com/${profileData.other_details.facebook}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            @{profileData.other_details.facebook}
                          </a>
                        }
                      />
                    )}
                  </div>
                  {/* Education Section */}
                  <div className="text-xl font-semibold pt-5 pb-2">Education</div>
                  {profileData?.educations && profileData.educations.length > 0 ? (
                    profileData.educations.map((edu, index) => (
                      <div key={index}>
                        <DetailItem
                          label=""
                          value={
                            edu.degree
                              ? `${edu.degree}${edu.university_name || edu.city
                                ? ` at ${edu.university_name || ""}${edu.university_name && edu.city ? ", " : ""}${edu.city || ""}`
                                : ""
                              }${edu.from_year
                                ? edu.to_year
                                  ? ` (${edu.from_year} to ${edu.to_year})`
                                  : ` (${edu.from_year})`
                                : edu.to_year
                                  ? ` (${edu.to_year})`
                                  : ""
                              }`
                              : null
                          }
                        />
                        {
                          edu.college && (
                            <DetailItem label="College Name" value={edu.college} />
                          )
                        }
                      </div>
                    ))
                  ) : (
                    <DetailItem label="" value="N/A" />
                  )}

                  {/* Schook Section */}
                  {profileData?.highschools?.length > 0 && (
                    <>
                      <div className="text-xl font-semibold pt-5 pb-2">School</div>
                      {profileData.highschools.map((edu, index) => (
                        <DetailItem
                          key={index}
                          label=""
                          value={`${edu.school_name || "N/A"}, ${edu.city || "N/A"} (${edu.from_year || "N/A"} to ${edu.to_year || "N/A"})`}
                        />
                      ))}
                    </>
                  )}

                  {/* Rewards Section */}
                  {profileData?.rewards && profileData.rewards.length > 0 && (
                    <>
                      <div className="text-xl font-semibold pt-5 pb-2">Rewards</div>
                      {profileData.rewards.map((edu, index) => (
                        <DetailItem
                          key={index}
                          label=""
                          value={`${edu.title || "N/A"} in ${edu.year || "N/A"}`}
                        />
                      ))}
                    </>
                  )}

                  {/* Awards Section */}
                  {profileData?.awards && profileData.awards.length > 0 && (
                    <>
                      <div className="text-xl font-semibold pt-5 pb-2">Awards</div>
                      {profileData.awards.map((edu, index) => (
                        <DetailItem
                          key={index}
                          label=""
                          value={`${edu.title || "N/A"} in ${edu.year || "N/A"}`}
                        />
                      ))}
                    </>
                  )}

                  {/* Medals Section */}
                  {profileData?.medals && profileData.medals.length > 0 && (
                    <>
                      <div className="text-xl font-semibold pt-5 pb-2">Medals</div>
                      {profileData.medals.map((edu, index) => (
                        <DetailItem
                          key={index}
                          label=""
                          value={`${edu.title || "N/A"} in ${edu.year || "N/A"}`}
                        />
                      ))}
                    </>
                  )}

                  {/* Certifications Section */}
                  {profileData?.certifications && profileData.certifications.length > 0 && (
                    <>
                      <div className="text-xl font-semibold pt-5 pb-2">Certifications</div>
                      {profileData.certifications.map((edu, index) => (
                        <DetailItem
                          key={index}
                          label=""
                          value={`${edu.title || "N/A"} in ${edu.year || "N/A"}`}
                        />
                      ))}
                    </>
                  )}

                  {/* Occupation Section */}
                  <div className="text-xl font-semibold pt-5 pb-2">Occupation</div>
                  {profileData?.occupations && profileData.occupations.length > 0 ? (
                    profileData.occupations.map((occ, index) => (
                      <DetailItem
                        key={index}
                        label=""
                        value={
                          occ.role
                            ? `${occ.role}${occ.company_name ? ` at ${occ.company_name}` : ""
                            }${occ.from_year
                              ? occ.to_year
                                ? ` (${occ.from_year} to ${occ.to_year})`
                                : ` (${occ.from_year})`
                              : occ.to_year
                                ? ` (${occ.to_year})`
                                : ""
                            }`
                            : occ.company_name
                              ? `${occ.company_name}${occ.from_year
                                ? occ.to_year
                                  ? ` (${occ.from_year} to ${occ.to_year})`
                                  : ` (${occ.from_year})`
                                : occ.to_year
                                  ? ` (${occ.to_year})`
                                  : ""
                              }`
                              : null
                        }
                      />
                    ))
                  ) : (
                    <DetailItem label="" value="N/A" />
                  )}

                  {/* Family Details Section */}
                  <div className="text-xl font-semibold pt-5 pb-2">Family Details</div>
                  {profileData?.family && (
                    <>
                      <DetailItem label="Father Name" value={profileData.family.father_name} />
                      <DetailItem label="Father Occupation" value={profileData.family.father_occupation} />
                      <DetailItem
                        label="Father Phone"
                        value={profileData.family.father_phone_number ? `${profileData.family.father_country_code || ""} ${profileData.family.father_phone_number}` : null}
                      />
                      <DetailItem label="Mother Name" value={profileData.family.mother_name} />
                      {
                        profileData.family.mother_occupation && (
                          <DetailItem label="Mother Occupation" value={profileData.family.mother_occupation} />
                        )
                      }
                      {
                        profileData.family.mother_phone_number && (
                          <DetailItem
                            label="Mother Phone"
                            value={profileData.family.mother_phone_number ? `${profileData.family.mother_country_code || ""} ${profileData.family.mother_phone_number}` : null}
                          />
                        )
                      }

                      <DetailItem label="Parents Native" value={profileData.family.parents_native} />
                      {/* <DetailItem label="Parents Cast" value={profileData.family.parents_cast} /> */}
                    </>
                  )}

                  {/* Maternal Details Section */}
                  <div className="text-xl font-semibold pt-5 pb-2">Maternal Details</div>
                  {profileData?.family && (
                    <>
                      {
                        profileData?.family.maternal_uncle_name && (
                          <DetailItem label="Uncle Name" value={profileData.family.maternal_uncle_name} />
                        )
                      }
                      {
                        profileData.family.maternal_uncle_occupation && (
                          <DetailItem label="Uncle Occupation" value={profileData.family.maternal_uncle_occupation} />
                        )
                      }
                      {
                        profileData?.family.maternal_phone_number && (
                          <DetailItem
                            label="Maternal Phone"
                            value={profileData.family.maternal_phone_number ? `${profileData.family.maternal_country_code || ""} ${profileData.family.maternal_phone_number}` : null}
                          />
                        )
                      }
                      <DetailItem label="Native" value={profileData.family.maternal_uncle_native_place} />
                    </>
                  )}

                  {/* Other Details Section */}
                  {
                    (profileData?.other_details?.partners_parents_origin_choice ||
                      profileData?.other_details?.expected_education ||
                      profileData?.other_details?.senders_name ||
                      profileData?.other_details?.senders_phone_number ||
                      profileData?.other_details?.person_to_contact_name ||
                      profileData?.other_details?.person_to_contact_phone) && (
                      <div className="text-xl font-semibold pt-5 pb-2">Other Details</div>
                    )
                  }

                  {profileData?.other_details && (
                    <>
                      {profileData.other_details.partners_parents_origin_choice && (
                        <DetailItem label="Partner's Parents Origin Choice" value={profileData.other_details.partners_parents_origin_choice} />
                      )}

                      {profileData.other_details?.expected_education && (
                        <DetailItem
                          label="Expected Education"
                          value={profileData.other_details?.expected_education}
                        />
                      )}
                      {profileData.other_details.senders_name && (
                        <DetailItem
                          label="Sender's Name"
                          value={profileData.other_details.senders_name}
                        />
                      )}

                      {
                        profileData.other_details.senders_phone_number && (
                          <DetailItem
                            label="Sender's Phone"
                            value={profileData.other_details.senders_phone_number ? `${profileData.other_details.senders_country_code || ""} ${profileData.other_details.senders_phone_number}` : null}
                          />
                        )
                      }

                      {
                        profileData.other_details.person_to_contact_name && (
                          <DetailItem label="Person to contact" value={profileData.other_details.person_to_contact_name} />
                        )
                      }
                      {
                        profileData.other_details.person_to_contact_phone && (
                          <DetailItem
                            label="Phone Number"
                            value={profileData.other_details.person_to_contact_phone ? `${profileData.other_details.person_to_contact_country_code || ""} ${profileData.other_details.person_to_contact_phone}` : null}
                          />
                        )
                      }
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MyProfile;